import { useCallback, useEffect, useMemo, useState } from "react";
import { IconType } from "react-icons";

import { ColumnDefinition } from "@/types/Table.type";
import { useToggleColumnVisibility } from "@/hooks/table/useToggleColumnVisibility";

export type TableMetaProps = {
  heading?: {
    icon?: IconType;
    className?: string;
    title?: string;
  };
  column?: {
    className?: string;
  };
  disableReorder?: boolean;
  disableTooltip?: boolean;
};

type UseTableConfigProps = {
  columns: ColumnDefinition[];
  initialColumnOrder: string[];
  onColumnVisibilityChanged?: (updateVisibility: Record<string, boolean>) => void;
  onColumnOrderChanged?: (updateOrder: string[]) => void;
  onRowOrderChanged?: (rowOrder: string[]) => void;
  onHiddenRowsChanged?: (hiddenRows: string[]) => void;
  onRowSelectionChanged?: (selection: Record<string, boolean>) => void;
};

export type UseTableConfigReturn = {
  toggleColumnVisibility: (column: ColumnDefinition) => void;
  columnVisibility: Record<string, boolean>;
  updateColumnOrder: (columnOrder: string[]) => void;
  columnOrder: string[];
  updateRowOrder: (rowOrder: string[]) => void;
  rowOrder: string[];
  hideRows: (rows: string[]) => void;
  hiddenRows: string[];
  setRowSelection: (selection: Record<string, boolean>) => void;
  clearRowSelection: () => void;
  rowSelection: Record<string, boolean>;
  addColumnsAfter: (columns: string[], afterColumn: string) => void;
};

export const useTableConfig = ({
  columns,
  initialColumnOrder,
  onColumnVisibilityChanged,
  onColumnOrderChanged,
  onRowOrderChanged,
  onHiddenRowsChanged,
  onRowSelectionChanged,
}: UseTableConfigProps): UseTableConfigReturn => {
  const _initialColumnVisibility = useMemo(
    () => columns.map((col) => [col.id, initialColumnOrder.includes(col.id)]),
    [initialColumnOrder, columns],
  );

  const [columnVisibility, setColumnVisibility] = useState(Object.fromEntries(_initialColumnVisibility));
  const [columnOrder, setColumnOrder] = useState<string[]>(initialColumnOrder);
  const [rowOrder, setRowOrder] = useState<string[]>([]);
  const [hiddenRows, setHiddenRows] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setColumnVisibility(Object.fromEntries(_initialColumnVisibility));
  }, [_initialColumnVisibility]);

  useEffect(() => {
    setColumnOrder(initialColumnOrder);
  }, [initialColumnOrder]);

  const _updateColumnVisibility = (columnVisibilities: Record<string, boolean>) => {
    setColumnVisibility(columnVisibilities);
    if (onColumnVisibilityChanged) onColumnVisibilityChanged(columnVisibilities);
  };

  const _updateColumnOrder = (newColumnOrder: string[]) => {
    setColumnOrder(newColumnOrder);
    if (onColumnOrderChanged) onColumnOrderChanged(newColumnOrder);
  };

  const _updateRowOrder = (rowOrder: string[]) => {
    setRowOrder(rowOrder);
    if (onRowOrderChanged) onRowOrderChanged(rowOrder);
  };

  const _hideRows = (rows: string[]) => {
    const allHiddenRows = [...new Set([...hiddenRows, ...rows])];
    setHiddenRows(allHiddenRows);
    if (onHiddenRowsChanged) onHiddenRowsChanged([...new Set(rows)]);
  };

  const _setRowSelection = (selection: Record<string, boolean>) => {
    setRowSelection(selection);
    if (onRowSelectionChanged) onRowSelectionChanged(selection);
  };

  const _clearRowSelection = () => {
    setRowSelection({});
  };

  // To Add/Make visible one or more columns to the column order right after the specified column
  const _showColumnsAfter = useCallback(
    (columns: string[], afterColumn: string) => {
      setColumnOrder((prev) => {
        // remove any old occurrences
        const filtered = prev.filter((c) => !columns.includes(c));
        const idx = filtered.indexOf(afterColumn);
        if (idx < 0) return prev;

        const newColumnOrder = [...filtered];
        newColumnOrder.splice(idx + 1, 0, ...columns);

        onColumnOrderChanged?.(newColumnOrder);
        return newColumnOrder;
      });
    },
    [setColumnOrder, onColumnOrderChanged],
  );

  const toggleColumnVisibility = useToggleColumnVisibility({
    columns,
    columnVisibility,
    setColumnVisibility: _updateColumnVisibility,
    columnOrder,
    setColumnOrder: _updateColumnOrder,
  });

  return {
    toggleColumnVisibility,
    columnVisibility,
    updateColumnOrder: _updateColumnOrder,
    columnOrder,
    updateRowOrder: _updateRowOrder,
    rowOrder,
    hideRows: _hideRows,
    hiddenRows,
    setRowSelection: _setRowSelection,
    clearRowSelection: _clearRowSelection,
    rowSelection,
    addColumnsAfter: _showColumnsAfter,
  };
};
